import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { apiService } from "../../utils/api";
import html2pdf from "html2pdf.js";
import { FaDownload } from "react-icons/fa";
import "../../components/ComparisonResults.css";

function DemoComparisonAudioResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [timeTaken, setTimeTaken] = useState("0:00");
  const [accuracy, setAccuracy] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const resultsContainerRef = useRef(null);

  useEffect(() => {
    console.log(location.state.data);

    // If we have comparison results from navigation state, use them
    if (location.state?.data) {
      setComparisonResults(location.state.data.comparison);

      setAccuracy(location.state.data.comparison.accuracy);
      setSpeed(location.state.data.comparison.typingSpeed);
      setTimeTaken(location.state.data.comparison.timeTaken);
      setDocument(location.state.data.document);
    }
  }, []);

  const renderFormattedText = () => {
    if (comparisonResults?.formattedText) {
      return <div dangerouslySetInnerHTML={{ __html: comparisonResults.formattedText }} />;
    } else if (comparisonResults?.wordByWordComparison) {
      // Render from word-by-word comparison if formattedText is not available
      return (
        <div className="formatted-text">
          {comparisonResults.wordByWordComparison.map((item, index) => {
            if (item.correct) {
              return (
                <span key={index} className="correct">
                  {item.student}{" "}
                </span>
              );
            } else if (item.student === null) {
              return (
                <span key={index} className="missing">
                  {item.original}{" "}
                </span>
              );
            } else if (item.original === null) {
              return (
                <span key={index} className="extra">
                  {item.student}{" "}
                </span>
              );
            } else {
              return (
                <span key={index}>
                  <span className="incorrect">{item.student} </span>
                  <span className="correction">({item.original}) </span>
                </span>
              );
            }
          })}
        </div>
      );
    }
  };

  const handleDownloadPdf = () => {
    if (!resultsContainerRef.current) return;

    setGeneratingPdf(true);

    const element = resultsContainerRef.current;
    const filename = `${document.title}_results_${new Date().toISOString().slice(0, 10)}.pdf`;

    // Create a clone of the element to modify for PDF without affecting the display
    const clonedElement = element.cloneNode(true);

    // Apply PDF-specific styling to the clone
    clonedElement.classList.add("generating-pdf");

    // Find the formatted content and adjust its styling
    const formattedContent = clonedElement.querySelector(".formatted-content");
    if (formattedContent) {
      formattedContent.style.minHeight = "auto";
      formattedContent.style.maxHeight = "none";
      formattedContent.style.height = "auto";
    }

    const opt = {
      margin: [10, 10, 10, 10],
      filename: filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .from(clonedElement)
      .set(opt)
      .save()
      .then(() => {
        setGeneratingPdf(false);
      })
      .catch((err) => {
        console.error("Error generating PDF:", err);
        setGeneratingPdf(false);
      });
  };

  return (
    <div className="comparison-results-page" style={{ marginTop: "50px" }}>
      <div className="results-actions">
        <button onClick={() => navigate("/")} className="back-button">
          Back to Home Page
        </button>
        <button onClick={handleDownloadPdf} className="download-pdf-button" disabled={generatingPdf}>
          <FaDownload /> {generatingPdf ? "Generating PDF..." : "Download as PDF"}
        </button>
      </div>

      <div className="comparison-results-container" ref={resultsContainerRef}>

        {comparisonResults && (
          <>
            <div className="practice-results-header">
              <h2>NEW PITMAN STENO TYPING INSTITUTE PRAYAGRAJ</h2>
              <h3>Practice Results: {comparisonResults.totalOriginalWords} words</h3>
            </div>

            <div className="student-info-container">
              <div className="student-info">
                <div className="info-row">
                  <span className="info-label">Student:</span>
                  <span className="info-value">XYZ</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Roll No:</span>
                  <span className="info-value">0000000</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Time taken:</span>
                  <span className="info-value">{timeTaken}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Accuracy:</span>
                  <span className="info-value">{accuracy}%</span>
                </div>
              </div>

              <div className="document-info">
                <div className="info-row">
                  <span className="info-label">Date:</span>
                  <span className="info-value">--</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Text:</span>
                  <span className="info-value">{comparisonResults.totalOriginalWords} words</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Speed:</span>
                  <span className="info-value">{speed} wpm</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Words:</span>
                  <span className="info-value">
                    {comparisonResults.totalStudentWords}/{comparisonResults.totalOriginalWords}
                  </span>
                </div>
              </div>
            </div>

            <div className="answer-with-corrections">
              <h3>Your Answer with Corrections:</h3>
              <div className="formatted-content">{renderFormattedText()}</div>
            </div>

            <div className="stats-container">
              <div className="stat-item">
                <span className="stat-label">Total Missing:</span>
                <span
                  className="stat-value"
                  style={{
                    color: comparisonResults.matchPercentage > 80 ? "#27ae60" : comparisonResults.matchPercentage > 50 ? "#f39c12" : "#e74c3c",
                  }}
                >
                  {comparisonResults.wordByWordComparison.filter((item) => item.student == null).length}
                </span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Total Wrong:</span>
                <span className="stat-value">{comparisonResults.wordByWordComparison.filter((item) => item.correct === false).length}</span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Dot Error:</span>
                <span className="stat-value">{comparisonResults.wordByWordComparison.filter((item) => (item.original === "." || item.original?.endsWith(".")) && item.correct === false).length * 3}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DemoComparisonAudioResults;
